import { ApexOptions } from 'apexcharts';
import NoSsr from 'core/components/NoSsr';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { WithStyles } from 'core/style/interfaces';
import { numberSeparateThousands } from 'core/utils';
import React, { Suspense } from 'react';
import { GetServiceMarketMapDataQuery } from 'site/graphql';
import { HighLowIndex } from '../high-low/const';
import { Time, timeMapper } from './const';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

type ITreemapChartProps = {
    securities: GetServiceMarketMapDataQuery['highLowData'];
    securityType: HighLowIndex;
    time: Time;
} & WithStyles<typeof styles>;

const options: ApexOptions = {
    chart: {
        height: 700,
        toolbar: {
            show: false,
        },
    },
    dataLabels: {
        enabled: true,
        formatter: (value, { w, seriesIndex, dataPointIndex }) => {
            return [value, w.config.series[seriesIndex].data[dataPointIndex].value.toFixed(2) + '%'] as any;
        },
        style: {
            fontSize: '12px',
        },
        offsetY: -4,
    },
    tooltip: {
        custom: ({ seriesIndex, dataPointIndex, w }) => {
            const item = w.config.series[seriesIndex].data[dataPointIndex];

            return `<div style="padding: 4px 12px; line-height: 20px;">
                <div>Код ценной бумаги: <strong>${item.x}</strong></div>
                <div>Наименование: <strong>${item.name}</strong></div>
                ${
                    item.securityType === HighLowIndex.ETF
                        ? ''
                        : `<div>Капитализация: <strong>${numberSeparateThousands(item.y.toFixed(0))}</strong></div>`
                }
                <div>Доходность ${timeMapper[item.time]}: <strong style="color: ${item.fillColor}">${item.value.toFixed(
                2,
            )}%</strong></div>
            </div>`;
        },
    },
};

/**
 * Получить цвет из диапазона (положительные числа всегда зеленые)
 */
const getColor = (value: number, ranges: number[]) => {
    const positive = value >= 0;
    const maxLightness = positive ? 65 : 80; // hsl(110, 100%, maxLightness)
    const stepLightness = 10; // hsl(110, 100%, maxLightness - stepLightness)
    const negativeCount = ranges.filter(v => v < 0).length; // 0-7
    const moreThanCount = ranges.filter(r => value >= r).length || 1; // 1-7
    const hue = positive ? 115 : 0; // H
    const saturation = positive ? 70 : 80; // S
    const lightness = positive
        ? maxLightness - (moreThanCount - 1 - negativeCount) * stepLightness
        : maxLightness - (negativeCount - moreThanCount) * stepLightness; // L

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Расчет точек легенды с запасом в половину шага с каждой стороны
 * @example (-40, 40, 5) => [-30, -15, 0, 15, 30]
 * @example (-60.79, 191.22, 7) => [-39.79, -4.79, 30.21, 65.21, 100.22, 135.22, 170.22]
 */
const calculateRanges = (min: number, max: number, count: number) => {
    const perfectStep = (Math.abs(min) + Math.abs(max)) / (count - 1);
    const halfPerfectStep = perfectStep / 2;
    const newMin = min + halfPerfectStep;
    const newMax = max - halfPerfectStep;
    const step = (Math.abs(newMin) + Math.abs(newMax)) / (count - 1);

    return new Array(count).fill(null).map((_, i) => newMin + step * i);
};

const TreemapChart: React.FC<ITreemapChartProps> = ({ securities, securityType, time, classes }) => {
    const { series, ranges } = React.useMemo(() => {
        const filteredSecurities = securities.filter(security => {
            switch (securityType) {
                case HighLowIndex.MoscowExchangesIndex:
                    return security.moscowExchangesIndex;
                case HighLowIndex.MoscowExchangeBlueChipIndex:
                    return security.moscowExchangeBlueChipIndex;
                case HighLowIndex.ETF:
                    return security.eTF;
                default:
                    return !security.eTF && !security.index;
            }
        });
        const ranges = calculateRanges(
            Math.min(...filteredSecurities.map(security => security[time])),
            Math.max(...filteredSecurities.map(security => security[time])),
            7,
        );

        return {
            ranges,
            series: [
                {
                    data: filteredSecurities
                        .sort((a, b) => b.issueCapitalization - a.issueCapitalization)
                        .map(security => ({
                            x: security.secId,
                            y: securityType === HighLowIndex.ETF ? 1 : security.issueCapitalization,
                            fillColor: getColor(security[time], ranges),
                            value: security[time],
                            securityType,
                            time,
                            name: security.name,
                        })),
                },
            ],
        };
    }, [securities, securityType, time]);

    return (
        <>
            <NoSsr>
                <Suspense fallback={<div>Загрузка...</div>}>
                    <ReactApexChart options={options} series={series} type={'treemap' as any} />
                </Suspense>
            </NoSsr>
            <div className={classes.legend}>
                <div style={{ background: getColor(ranges[0], ranges) }}>{ranges[0].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[1], ranges) }}>{ranges[1].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[2], ranges) }}>{ranges[2].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[3], ranges) }}>{ranges[3].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[4], ranges) }}>{ranges[4].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[5], ranges) }}>{ranges[5].toFixed(2)}%</div>
                <div style={{ background: getColor(ranges[6], ranges) }}>{ranges[6].toFixed(2)}%</div>
            </div>
        </>
    );
};

const styles = createStyles({
    legend: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(auto, 70px))',
        gap: 2 + 'px',
        color: 'white',
        textAlign: 'center',
        fontSize: 0.7 + 'rem',
        justifyContent: 'flex-end',
    },
});

export default injectSheet(styles)(TreemapChart);
